import React from "react";

import Navbar from "../../components/en/Navbar";
import Footer from "../../components/en/Footer";
import CtaNomal from "../../components/en/CtaNomal";

import "../../styles/common.css";
import "../../styles/work.css";

import { works, resolveWork } from "../../data/works";
import { tagLabels } from "../../data/tagLabels";

const lang = "en";

const resolvedWorks = works.map(resolveWork);

const buildDisplayTags = (w) => {
  const industry = (w.industryTags ?? []).slice(0, 2);
  const companyCountryTag = w.company?.country ? [`companyCountry:${w.company.country}`] : [];
  return [...industry, ...companyCountryTag];
};

const getLabel = (keyOrText) => tagLabels?.[keyOrText]?.[lang] ?? keyOrText;

function uniq(arr) {
  return Array.from(new Set(arr));
}

function FilterDropdownMulti({ title, options, selectedValues = [], onToggle }) {
  const [open, setOpen] = React.useState(false);
  const [isMobile, setIsMobile] = React.useState(false);
  const boxRef = React.useRef(null);
  const selectedSet = React.useMemo(() => new Set(selectedValues), [selectedValues]);

  React.useEffect(() => {
    const mq = window.matchMedia("(max-width: 768px)");
    const update = () => setIsMobile(mq.matches);
    update();
    mq.addEventListener?.("change", update);
    return () => mq.removeEventListener?.("change", update);
  }, []);

  React.useEffect(() => {
    if (!open) return;
    if (isMobile) return;
    const onDown = (e) => {
      if (!boxRef.current) return;
      if (!boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open, isMobile]);

  React.useEffect(() => {
    if (!open) return;
    if (!isMobile) return;
    const body = document.body;
    const scrollY = window.scrollY;
    body.style.position = "fixed";
    body.style.top = `-${scrollY}px`;
    body.style.left = "0";
    body.style.right = "0";
    body.style.width = "100%";
    body.style.overflow = "hidden";
    return () => {
      const y = Math.abs(parseInt(body.style.top || "0", 10)) || 0;
      body.style.position = "";
      body.style.top = "";
      body.style.left = "";
      body.style.right = "";
      body.style.width = "";
      body.style.overflow = "";
      window.scrollTo(0, y);
    };
  }, [open, isMobile]);

  const isActive = selectedValues.length > 0;
  const handleToggle = (value) => {
    if (selectedSet.has(value)) onToggle(value, "remove");
    else onToggle(value, "add");
  };

  return (
    <div className={`filterDD ${isMobile ? "is-mobile" : ""}`} ref={boxRef}>
      <button
        type="button"
        className={`filterDD-trigger ${isActive ? "is-active" : ""}`}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <span className="filterDD-title">{title}</span>
        {isActive && <span className="filterDD-count">{selectedValues.length}</span>}
      </button>

      {open && (
        <div className={`filterDD-panel ${isMobile ? "is-popup" : ""}`}>
          <div className="filterDD-popupHeader">
            <p className="filterDD-popupTitle">{title}</p>
            <div className="filterDD-close__box">
              <button type="button" className="filterDD-close" onClick={() => setOpen(false)} />
            </div>
          </div>
          <div className="filterDD-buttons">
            {options.map((opt) => {
              const isOn = selectedSet.has(opt.value);
              return (
                <button
                  key={opt.value}
                  type="button"
                  className={`filterDD-optionBtn ${isOn ? "is-selected" : ""}`}
                  onClick={() => handleToggle(opt.value)}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
        </div>
      )}
      {open && isMobile && (
        <button type="button" className="filterDD-backdrop" aria-label="Close" onClick={() => setOpen(false)} />
      )}
    </div>
  );
}


export default function WorkEn() {
  // --- options生成 ---
  const categoryOptions = uniq(resolvedWorks.map((w) => w.category).filter(Boolean)).map((v) => ({
  value: v,
  label: getLabel(`category:${v}`),
}));

  const serviceOptions = uniq(resolvedWorks.flatMap((w) => w.serviceTags ?? [])).map((v) => ({
    value: v,
    label: getLabel(v),
  }));

  const purposeOptions = uniq(resolvedWorks.flatMap((w) => w.purposeTags ?? [])).map((v) => ({
    value: v,
    label: getLabel(v),
  }));

  const industryOptions = uniq(resolvedWorks.flatMap((w) => w.industryTags ?? [])).map((v) => ({
    value: v,
    label: getLabel(v),
  }));

  const targetKeys = uniq(
    resolvedWorks.flatMap((w) => [
      ...(w.target?.regions ?? []).map((r) => `targetRegion:${r}`),
      ...(w.target?.countries ?? []).map((c) => `targetCountry:${c}`),
    ])
  );
  const targetOptions = targetKeys.map((v) => ({
    value: v,
    label: getLabel(v),
  }));

  // --- 複数選択 state（配列）---
  const [selectedCategories, setSelectedCategories] = React.useState([]);
  const [selectedServices, setSelectedServices] = React.useState([]);
  const [selectedPurposes, setSelectedPurposes] = React.useState([]);
  const [selectedIndustries, setSelectedIndustries] = React.useState([]);
  const [selectedTargets, setSelectedTargets] = React.useState([]);

  const clearAll = () => {
    setSelectedCategories([]);
    setSelectedServices([]);
    setSelectedPurposes([]);
    setSelectedIndustries([]);
    setSelectedTargets([]);
  };

  const toggleSelection = (setter, value, action) => {
    setter((prev) => {
      if (action === "add") return prev.includes(value) ? prev : [...prev, value];
      return prev.filter((v) => v !== value);
    });
  };

  const anyFilterActive =
    selectedCategories.length > 0 ||
    selectedServices.length > 0 ||
    selectedPurposes.length > 0 ||
    selectedIndustries.length > 0 ||
    selectedTargets.length > 0;

  const selectedChips = [
    ...selectedCategories.map((v) => ({ type: "category", typeLabel: "Category", value: v, label: getLabel(`category:${v}`) })),
    ...selectedServices.map((v) => ({ type: "service", typeLabel: "Project Details", value: v, label: getLabel(v) })),
    ...selectedPurposes.map((v) => ({ type: "purpose", typeLabel: "Objective", value: v, label: getLabel(v) })),
    ...selectedIndustries.map((v) => ({ type: "industry", typeLabel: "Industry", value: v, label: getLabel(v) })),
    ...selectedTargets.map((v) => ({ type: "target", typeLabel: "Service Area", value: v, label: getLabel(v) })),
  ];

  const removeChip = (chip) => {
    const setters = {
      category: setSelectedCategories,
      service: setSelectedServices,
      purpose: setSelectedPurposes,
      industry: setSelectedIndustries,
      target: setSelectedTargets,
    };
    setters[chip.type]((prev) => prev.filter((v) => v !== chip.value));
  };

  const filteredWorks = resolvedWorks.filter((w) => {
    if (!anyFilterActive) return true;
    const wCategory = w.category ?? null;
    const wService = w.serviceTags ?? [];
    const wPurpose = w.purposeTags ?? [];
    const wIndustry = w.industryTags ?? [];
    const wTarget = [
      ...(w.target?.regions ?? []).map((r) => `targetRegion:${r}`),
      ...(w.target?.countries ?? []).map((c) => `targetCountry:${c}`),
    ];
    const hitCategory = selectedCategories.length === 0 || (wCategory && selectedCategories.includes(wCategory));
    const hitService = selectedServices.length === 0 || selectedServices.some((s) => wService.includes(s));
    const hitPurpose = selectedPurposes.length === 0 || selectedPurposes.some((p) => wPurpose.includes(p));
    const hitIndustry = selectedIndustries.length === 0 || selectedIndustries.some((i) => wIndustry.includes(i));
    const hitTarget = selectedTargets.length === 0 || selectedTargets.some((t) => wTarget.includes(t));
    return hitCategory && hitService && hitPurpose && hitIndustry && hitTarget;
  });

  const [listAnimating, setListAnimating] = React.useState(false);
  React.useEffect(() => {
    setListAnimating(true);
    const t = setTimeout(() => setListAnimating(false), 220);
    return () => clearTimeout(t);
  }, [selectedCategories, selectedServices, selectedPurposes, selectedIndustries, selectedTargets]);

  return (
    <>
      <Navbar />
      <main className='workList'>
        <section className="fvUnder">
          <div className="fvUnder-text">
            <h1>Portfolio</h1>
            <nav aria-label="Breadcrumb" className="breadcrumb">
              <ol className="breadcrumb">
                <li>
                  <a href="/en">Home</a>
                </li>
                <li aria-current="page">Portfolio</li>
              </ol>
            </nav>
          </div>
        </section>

        {/* フィルター */}
        <section className="workFilters">
          <div className="workFilters-bar">
            <FilterDropdownMulti
              title="Category"
              options={categoryOptions}
              selectedValues={selectedCategories}
              onToggle={(v, action) => toggleSelection(setSelectedCategories, v, action)}
            />
            <FilterDropdownMulti
              title="Project Details"
              options={serviceOptions}
              selectedValues={selectedServices}
              onToggle={(v, action) => toggleSelection(setSelectedServices, v, action)}
            />
            <FilterDropdownMulti
              title="Objective"
              options={purposeOptions}
              selectedValues={selectedPurposes}
              onToggle={(v, action) => toggleSelection(setSelectedPurposes, v, action)}
            />
            <FilterDropdownMulti
              title="Industry"
              options={industryOptions}
              selectedValues={selectedIndustries}
              onToggle={(v, action) => toggleSelection(setSelectedIndustries, v, action)}
            />
            <FilterDropdownMulti
              title="Service Area"
              options={targetOptions}
              selectedValues={selectedTargets}
              onToggle={(v, action) => toggleSelection(setSelectedTargets, v, action)}
            />

            {/* ✅ メニュー外：全クリア */}
            {anyFilterActive && (
              <button type="button" className="workFilters-reset" onClick={clearAll}>
                Clear all
              </button>
            )}
          </div>

        </section>


        <section className="work-list">
          {selectedChips.length > 0 && (
            <div className="workSelected">
              <p className="workSelected-label">Selected</p>
              <div className="workSelected-chips">
                {selectedChips.map((chip) => (
                  <span key={`${chip.type}:${chip.value}`} className="workSelected-chip">
                    <span className="workSelected-chip-type">{chip.typeLabel}</span>
                    <span className="workSelected-chip-label">{chip.label}</span>
                    <button
                      type="button"
                      className="workSelected-chip-remove"
                      onClick={() => removeChip(chip)}
                      aria-label={`Remove ${chip.label}`}
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            </div>
          )}
          <div className={`work-box ${listAnimating ? "is-animating" : ""}`}>
            {filteredWorks.map((w) => {
              const displayTags = buildDisplayTags(w);

              return (
                <article key={w.slug}>
                  <a href={`/en/work/${w.slug}`}>
                    <div className="work-box__image">
                      <img src={w.thumbnail} alt={w.title?.[lang] ?? ""} />
                    </div>

                    <div className="work-box__text">
                      <h3>{w.title?.[lang]}</h3>

                      {displayTags.length > 0 && (
                        <ul className="work-box__tag">
                          {displayTags.map((tag) => (
                            <li key={tag}>{getLabel(tag)}</li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </a>
                </article>
              );
            })}
          </div>
        </section>

        <CtaNomal />
      </main>
      <Footer />
    </>
  );
}
